import React, { useMemo, useState } from "react";
import { InlineModerationRating } from "./InlineModerationRating";
import ConfirmDialog from "../../utils/ConfirmDialog";
import { DeleteIcon, EmptyState, Pagination, ScopeCell, SortIcon, TitleCell, formatDate } from "./moderationShared";

const UnverifiedTable = ({
  unverifiedPDFs,
  filteredAndSortedData,
  getPublicUrl,
  approvePDF,
  deletePDF,
  requestSort,
  sortConfig,
  showAssignment,
  assignedTo,
  isNew,
  onRename,
}) => {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [pendingDelete, setPendingDelete] = useState(null);

  const visible = useMemo(() => filteredAndSortedData(unverifiedPDFs), [unverifiedPDFs, filteredAndSortedData]);
  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageItems = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const sortableHeader = (key, label) => (
    <th onClick={() => requestSort(key)} className="px-4 py-3 cursor-pointer text-xs font-semibold text-gray-500 uppercase tracking-wider select-none hover:text-gray-700 transition-colors">
      {label} <SortIcon sortConfig={sortConfig} columnKey={key} />
    </th>
  );

  return (
    <div>
      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete Resource"
        message={`Are you sure you want to delete "${pendingDelete?.title || 'this resource'}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => { deletePDF(pendingDelete); setPendingDelete(null); }}
        onCancel={() => setPendingDelete(null)}
      />
      {visible.length > 0 ? (
        <div>
          {/* Desktop/tablet table */}
          <div className="hidden md:block rounded-xl border border-gray-100 overflow-hidden shadow-card">
            <table className="w-full table-fixed text-sm text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {sortableHeader('title', 'Title')}
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tags</th>
                  {sortableHeader('subject', 'Scope')}
                  {sortableHeader('userID', 'Uploaded By')}
                  {sortableHeader('modifiedAt', 'Date')}
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Mod. Rating</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                {pageItems.map((pdf) => (
                  <tr key={pdf.path || pdf.id} className="hover:bg-gray-50/70 transition-colors">
                    <td className="px-4 py-3 break-words" style={{wordBreak:'break-word'}}>
                      <TitleCell pdf={pdf} getPublicUrl={getPublicUrl} isNew={isNew} onRename={onRename} />
                    </td>
                    <td className="px-4 py-3 break-words" style={{wordBreak:'break-word'}}>
                      <div className="flex flex-wrap gap-1">
                        {pdf.tags ? pdf.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="inline-block px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">{tag}</span>
                        )) : <span className="text-gray-300">—</span>}
                        {pdf.tags && pdf.tags.length > 3 && <span className="text-xs text-gray-400">+{pdf.tags.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <ScopeCell pdf={pdf} showAssignment={showAssignment} assignedTo={assignedTo} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs break-words" style={{wordBreak:'break-word'}}>{pdf.userID || 'Unknown'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(pdf.modifiedAt)}</td>
                    <td className="px-4 py-3">
                      <InlineModerationRating document={pdf} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          className="px-2.5 py-1 text-xs font-medium text-white bg-emerald-500 hover:bg-emerald-600 rounded-md transition-colors"
                          onClick={() => approvePDF(pdf)}
                        >Approve</button>
                        <button
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          onClick={() => setPendingDelete(pdf)}
                        >
                          <DeleteIcon />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {pageItems.map((pdf) => (
              <div key={pdf.path || pdf.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-card">
                <div className="text-sm break-words">
                  <TitleCell pdf={pdf} getPublicUrl={getPublicUrl} isNew={isNew} onRename={onRename} />
                </div>
                <div className="mt-2">
                  <ScopeCell pdf={pdf} showAssignment={showAssignment} assignedTo={assignedTo} />
                </div>
                {pdf.tags && pdf.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {pdf.tags.slice(0, 4).map((tag, i) => (
                      <span key={i} className="inline-block px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">{tag}</span>
                    ))}
                    {pdf.tags.length > 4 && <span className="text-xs text-gray-400">+{pdf.tags.length - 4}</span>}
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-2 break-words">
                  <span>By {pdf.userID || 'Unknown'}</span> · <span>{formatDate(pdf.modifiedAt)}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-50">
                  <span className="text-xs text-gray-400 block mb-1">Moderator Rating</span>
                  <InlineModerationRating document={pdf} />
                </div>
                <div className="mt-3 pt-3 border-t border-gray-50 flex flex-wrap gap-1.5">
                  <button className="px-2.5 py-1 text-xs font-medium text-white bg-emerald-500 hover:bg-emerald-600 rounded-md transition-colors" onClick={() => approvePDF(pdf)}>Approve</button>
                  <button className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" onClick={() => setPendingDelete(pdf)}>
                    <DeleteIcon />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <Pagination page={currentPage} pageSize={pageSize} total={visible.length} totalPages={totalPages} setPage={setPage} />
        </div>
      ) : unverifiedPDFs.length > 0 ? (
        <EmptyState title="No matches" message="No unverified resources match the current search or filters." />
      ) : (
        <EmptyState title="All caught up" message="No unverified resources to review." />
      )}
    </div>
  );
};

export default UnverifiedTable;
